import { defineStore } from 'pinia'
import { computed, ref, shallowRef } from 'vue'
import { Dialog, Screen, type DialogChainObject } from 'quasar'
import { router } from 'src/router'
import {
  getInbox,
  getConversation,
  getUnreadCounts,
  sendMessage,
  sendBroadcastMessage,
  startDirectConversation,
  editMessage,
  markMessageAsRead,
  markConversationAsUnread,
  markMessageAsUnread,
  markAllAsRead,
  markAllAnnouncementsRead,
  markAllInboxAsRead,
  undoMarkAllInboxAsRead,
  getAnnouncements,
  sendAnnouncement,
  deleteAnnouncement,
  editAnnouncement,
  searchMessages,
  ConversationScope,
  SystemMessageType,
  type InboxItemModel,
  type ConversationModel,
  type MessageModel,
  type SendMessageRequest,
  type SendBroadcastMessageRequest,
  type StartDirectConversationRequest,
  type AnnouncementModel,
  type SendAnnouncementRequest,
  type EditAnnouncementRequest,
  type BroadcastMessageModel,
  type BroadcastConversationModel,
  type SearchMessagesParams,
  getBroadcastMessage,
  getBroadcastConversation,
  acknowledgeUrgentConversation,
  createGroupChat,
  getGroupChat,
  updateGroupChat,
  addGroupChatMembers,
  removeGroupChatMember,
  leaveGroupChat,
  updateGroupChatMember,
  updateGroupChatAdmins,
  setGroupChatHidden,
  deleteGroupChat,
  type GroupChatModel,
  type CreateGroupChatRequest,
  type UpdateGroupChatRequest,
  type AddGroupChatMembersRequest,
  type RemoveGroupChatMemberRequest,
  type UpdateGroupChatMemberRequest,
} from 'src/service/messaging'
import type {
  NewMessageSignalRPayload,
  GroupChatSystemMessageSignalRPayload,
  MessageReadSignalRPayload,
  MessageUnreadSignalRPayload,
  MessageEditedSignalRPayload,
  NewAnnouncementSignalRPayload,
  BroadcastSendingCompletePayload,
} from 'src/signalR/types'
import { useUserConfigStore } from 'src/common/store/user-config.store'
import { useMessagingStore } from 'src/stores/messaging.store'
import { useAuthStore, useNotificationSettingsStore } from 'src/common/store'
import {
  useClientMessagingStore,
  useClientContextStore,
} from 'src/client/stores'
import MessageToastDialog from 'src/components/messaging/MessageToastDialog.vue'
import { STATUS_CODES } from 'src/config/constants'
import { queryClient } from 'src/boot/vue-query'
import { QueryKey } from 'src/hooks/constants/queryKeys'
import { fail } from 'src/utils/notify'
import {
  NotificationSoundKey,
  URGENT_MESSAGE_NOTIFICATION_SETTINGS,
} from 'src/interfaces/notificationPreferences'
import { playNotificationSound } from 'src/utils/notificationSounds'

const urgentMessageSoundSetting = URGENT_MESSAGE_NOTIFICATION_SETTINGS.find(
  (setting) => setting.soundKey === NotificationSoundKey.UrgentMessage,
)

// ========================
// Urgent Message Types
// ========================
export interface UrgentMessage {
  id: string
  conversationId: string
  senderName: string
  senderId: string
  content: string
  receivedAt: Date
}

// Extended message type for optimistic updates with send status
export interface PendingMessageModel extends MessageModel {
  sendStatus: 'sending' | 'sent' | 'failed'
}

/** Internal unread counts state derived from InboxResponse */
interface InboxUnreadCounts {
  totalUnreadCount: number
  unreadByScope: Record<string, number>
  announcementUnreadCount: number
}

export const useMessagingServiceStore = defineStore('messaging.service', () => {
  // ========================
  // Private helpers
  // ========================
  function shouldPlayUrgentMessageSound(defaultEnabled: boolean) {
    if (!urgentMessageSoundSetting) {
      return defaultEnabled
    }

    const notificationSettingsStore = useNotificationSettingsStore()

    return notificationSettingsStore.isNotificationSoundEnabled(
      urgentMessageSoundSetting.key,
      urgentMessageSoundSetting.platformKeys,
      defaultEnabled,
    )
  }

  function showMessageToast(opts: {
    senderName: string
    content: string
    isUrgent: boolean
    conversationId: string
    centerId?: string
    onDismiss?: () => void
  }) {
    if (opts.isUrgent && isUrgentToastActive.value) {
      urgentToastQueue.value = [
        ...urgentToastQueue.value,
        {
          senderName: opts.senderName,
          content: opts.content,
          conversationId: opts.conversationId,
          onDismiss: opts.onDismiss,
        },
      ]
      return
    }

    if (!opts.isUrgent && isRegularToastActive.value) {
      regularToastQueue.value = [
        ...regularToastQueue.value,
        {
          senderName: opts.senderName,
          content: opts.content,
          conversationId: opts.conversationId,
        },
      ]
      return
    }

    if (opts.isUrgent) {
      isUrgentToastActive.value = true
    } else {
      isRegularToastActive.value = true
    }

    const toastDialog = Dialog.create({
      component: MessageToastDialog,
      componentProps: {
        senderName: opts.senderName,
        content: opts.content,
        isUrgent: opts.isUrgent,
        conversationId: opts.conversationId,
        crossCenterId: opts.centerId,
      },
    })

    activeToastDialogs.value = [...activeToastDialogs.value, toastDialog]

    toastDialog.onOk((result: { action: string }) => {
      activeToastDialogs.value = activeToastDialogs.value.filter(
        (dialog) => dialog !== toastDialog,
      )
      opts.onDismiss?.()

      if (opts.isUrgent) {
        isUrgentToastActive.value = false
        if (result.action === 'dismiss') {
          showNextUrgentToast()
        } else {
          urgentToastQueue.value = []
        }
      } else {
        isRegularToastActive.value = false
        if (result.action === 'dismiss' || result.action === 'timeout') {
          showNextRegularToast()
        } else {
          regularToastQueue.value = []
        }
      }

      if (result.action === 'reply') {
        if (opts.centerId) {
          // Cross-center: switch center and navigate
          const authStore = useAuthStore()
          authStore.setCurrentCenterId(opts.centerId)
          window.location.reload()
          return
        }
        const messagingUiStore = useMessagingStore()
        const currentRoute = router.currentRoute.value
        const isMessagingFullPage =
          currentRoute.name === 'admin.communicate' &&
          (!currentRoute.query.tab || currentRoute.query.tab === 'messaging')

        if (isMessagingFullPage) {
          messagingUiStore.closeMessager()
          void router.replace({
            name: 'admin.communicate',
            query: {
              ...currentRoute.query,
              tab: 'messaging',
              conversationId: opts.conversationId,
            },
          })
        } else if (Screen.lt.md) {
          void router.push({
            name: 'admin.communicate',
            query: { tab: 'messaging', conversationId: opts.conversationId },
          })
        } else {
          messagingUiStore.openToConversation(opts.conversationId)
        }
      } else if (result.action === 'settings') {
        void router.push({
          name: 'admin.settings.notifications',
          query: { section: 'messaging' },
        })
      }
    })
  }

  function showNextRegularToast() {
    if (regularToastQueue.value.length > 0) {
      const [next, ...rest] = regularToastQueue.value
      regularToastQueue.value = rest
      showMessageToast({
        ...next,
        isUrgent: false,
      })
    }
  }

  function showNextUrgentToast() {
    if (urgentToastQueue.value.length > 0) {
      const [next, ...rest] = urgentToastQueue.value
      urgentToastQueue.value = rest
      showMessageToast({
        ...next,
        isUrgent: true,
      })
    }
  }

  // ========================
  // State
  // ========================
  const isInitialized = ref(false)

  // Granular loading states — use these in UI to avoid over-triggering spinners
  const isInitializing = ref(false)
  const isRefreshingInbox = ref(false)
  const isLoadingConversation = ref(false)
  const isLoadingMoreMessages = ref(false)
  const isSendingBroadcast = ref(false)
  const isStartingDirectConversation = ref(false)
  const isLoadingAnnouncements = ref(false)
  const isLoadingMoreAnnouncements = ref(false)
  const isSendingAnnouncement = ref(false)
  const isLoadingBroadcastMessage = ref(false)
  const isSearchingMessages = ref(false)

  const error = ref<string | null>(null)
  let initializationPromise: Promise<void> | undefined
  const activeToastDialogs = shallowRef<DialogChainObject[]>([])

  /**
   * Conversation IDs the server has told us the current user is not permitted to
   * access. Used to render a graceful "no permission" view instead of
   * a broken / empty conversation, e.g. when a room staff opens a link to an
   * admin-scope thread, or a family member pastes someone else's conversation URL.
   */
  const forbiddenConversationIds = ref<Set<string>>(new Set())

  /**
   * Indicates the inbox + directory data may be out of date due to attendance
   * changes that affect room staff visibility. Set when attendance updates
   * arrive while messaging is closed (or when a forbidden conversation load
   * reveals access has changed). Cleared after refreshing.
   *
   * Only meaningful for room staff — higher-level roles aren't subject to the
   * same attendance-driven restrictions.
   */
  const isInboxStale = ref(false)

  // Inbox state
  const inbox = shallowRef<InboxItemModel[]>([])
  const unreadCounts = ref<InboxUnreadCounts>({
    totalUnreadCount: 0,
    unreadByScope: {},
    announcementUnreadCount: 0,
  })

  // Active conversation state (fullPage = default, drawer = messaging drawer)
  const activeConversationId = ref<string | null>(null)
  const activeConversation = shallowRef<ConversationModel | null>(null)
  const drawerConversationId = ref<string | null>(null)
  const drawerConversation = shallowRef<ConversationModel | null>(null)
  const conversationCache = ref<Map<string, ConversationModel>>(new Map())
  const groupChatDisplayNames = ref<Map<string, string>>(new Map())

  // Announcements state
  const announcements = shallowRef<AnnouncementModel[]>([])
  const pinnedAnnouncementsState = shallowRef<AnnouncementModel[]>([])
  const announcementsContinuationToken = ref<string | null>(null)
  // Signals that a new announcement arrived via SignalR (cleared by the drawer when the tab is visited)
  const hasNewAnnouncementSignal = ref(0)

  // Broadcast message viewing
  const activeBroadcastMessage = shallowRef<BroadcastMessageModel | null>(null)

  // Broadcast conversation (history of messages to same recipients)
  const activeBroadcastConversation =
    shallowRef<BroadcastConversationModel | null>(null)
  const isLoadingBroadcastConversation = ref(false)

  // Broadcast sending status tracking
  // Key: broadcastConversationId, Value: sending status
  const broadcastSendingStatuses = ref<
    Map<
      string,
      {
        status: 'sending' | 'complete'
        totalRecipients: number
        successCount: number
        failedCount: number
        failedRecipients: string[]
      }
    >
  >(new Map())

  // Pending messages (optimistic updates)
  const pendingMessages = ref<Map<string, PendingMessageModel>>(new Map())

  // Urgent messages state
  const urgentMessages = ref<UrgentMessage[]>([])
  const acknowledgedMessageIds = ref<Set<string>>(new Set())

  // Toast queue state
  const regularToastQueue = ref<
    Array<{
      senderName: string
      content: string
      conversationId: string
    }>
  >([])
  const urgentToastQueue = ref<
    Array<{
      senderName: string
      content: string
      conversationId: string
      onDismiss?: () => void
    }>
  >([])
  const isRegularToastActive = ref(false)
  const isUrgentToastActive = ref(false)

  // Whether the user is scrolled to the bottom of the active conversation
  // Set by ConversationView to enable scroll-aware unread tracking
  const isNearBottom = ref(true)

  // Latest incoming message notification (for toast display in layout)
  const latestIncomingMessage = ref<{
    conversationId: string
    messageId: string
    senderName: string
    content: string
    isUrgent: boolean
    isSystem?: boolean
    timestamp: number
  } | null>(null)

  // Mark-all-as-read undo state
  const readAllUndoToken = ref<string | null>(null)
  const readAllUndoModifiedCount = ref(0)
  const readAllUndoSnapshot = shallowRef<InboxItemModel[]>([])
  const readAllUndoPreviousCounts = ref<InboxUnreadCounts | null>(null)

  // ========================
  // Getters
  // ========================
  // True when any async operation is in flight — kept for backward compatibility
  const isLoading = computed(
    () =>
      isInitializing.value ||
      isRefreshingInbox.value ||
      isLoadingConversation.value ||
      isLoadingMoreMessages.value ||
      isSendingBroadcast.value ||
      isStartingDirectConversation.value ||
      isLoadingAnnouncements.value ||
      isLoadingMoreAnnouncements.value ||
      isSendingAnnouncement.value ||
      isLoadingBroadcastMessage.value ||
      isLoadingBroadcastConversation.value ||
      isSearchingMessages.value,
  )

  const hasUnreadMessages = computed(
    () => unreadCounts.value.totalUnreadCount > 0,
  )

  const regularToastQueueSize = computed(() => regularToastQueue.value.length)
  const urgentToastQueueSize = computed(() => urgentToastQueue.value.length)

  // Alias for inbox - used by new messaging components
  const inboxItems = computed(() => inbox.value)

  // Check if there are more messages to load in active conversation
  const hasMoreMessages = computed(() => {
    if (!activeConversation.value) return false
    return !!activeConversation.value.continuationToken
  })

  const roomInboxItems = computed(() =>
    inbox.value.filter((item) => item.scope === ConversationScope.Room),
  )

  const adminInboxItems = computed(() =>
    inbox.value.filter((item) => item.scope === ConversationScope.Admin),
  )

  const familyInboxItems = computed(() =>
    inbox.value.filter(
      (item) =>
        item.scope === ConversationScope.Room ||
        item.scope === ConversationScope.Admin,
    ),
  )

  const directInboxItems = computed(() =>
    inbox.value.filter((item) => item.scope === ConversationScope.Direct),
  )

  const internalInboxItems = computed(() =>
    inbox.value.filter((item) => item.scope === ConversationScope.Internal),
  )

  const groupChatInboxItems = computed(() =>
    inbox.value.filter(
      (item) =>
        item.scope === ConversationScope.GroupChat && !item.hiddenFromDirectory,
    ),
  )

  const hiddenGroupChatInboxItems = computed(() =>
    inbox.value.filter(
      (item) =>
        item.scope === ConversationScope.GroupChat && item.hiddenFromDirectory,
    ),
  )

  const activeMessages = computed(() => {
    if (!activeConversation.value) return []

    const messages = [...activeConversation.value.messages]

    // Add pending messages for this conversation
    pendingMessages.value.forEach((msg) => {
      if (msg.conversationId === activeConversationId.value) {
        // Replace existing if found, or add if not
        const existingIndex = messages.findIndex((m) => m.id === msg.id)
        if (existingIndex === -1) {
          messages.push(msg)
        }
      }
    })

    // Sort by date
    return messages.sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    )
  })

  function getActiveSlot(context: 'drawer' | 'fullPage') {
    return context === 'drawer'
      ? { id: drawerConversationId, conv: drawerConversation }
      : { id: activeConversationId, conv: activeConversation }
  }

  function isViewingConversation(conversationId: string): boolean {
    return (
      activeConversationId.value === conversationId ||
      drawerConversationId.value === conversationId
    )
  }

  function updateActiveSlots(
    conversationId: string,
    updatedConversation: ConversationModel,
  ) {
    if (activeConversationId.value === conversationId) {
      activeConversation.value = updatedConversation
    }
    if (drawerConversationId.value === conversationId) {
      drawerConversation.value = updatedConversation
    }
  }

  function updateInboxPartnerName(conversationId: string, partnerName: string) {
    const idx = inbox.value.findIndex(
      (i) => i.conversationId === conversationId,
    )
    if (idx < 0) return

    const items = [...inbox.value]
    items[idx] = { ...items[idx], partnerName }
    inbox.value = items
  }

  function setGroupChatDisplayName(
    conversationId: string,
    displayName: string | null | undefined,
  ) {
    const trimmedName = displayName?.trim()
    if (!trimmedName) return

    const next = new Map(groupChatDisplayNames.value)
    next.set(conversationId, trimmedName)
    groupChatDisplayNames.value = next
  }

  function updateConversationParticipantName(
    conversationId: string,
    participantName: string,
  ) {
    const cachedConversation = conversationCache.value.get(conversationId)

    if (cachedConversation) {
      const updatedConversation: ConversationModel = {
        ...cachedConversation,
        participantName,
      }
      conversationCache.value.set(conversationId, updatedConversation)
      updateActiveSlots(conversationId, updatedConversation)
      return
    }

    if (
      activeConversationId.value === conversationId &&
      activeConversation.value
    ) {
      activeConversation.value = {
        ...activeConversation.value,
        participantName,
      }
    }

    if (
      drawerConversationId.value === conversationId &&
      drawerConversation.value
    ) {
      drawerConversation.value = {
        ...drawerConversation.value,
        participantName,
      }
    }
  }

  const pinnedAnnouncements = computed(() => pinnedAnnouncementsState.value)

  const regularAnnouncements = computed(() =>
    announcements.value.filter((a) => !a.isPinned),
  )

  // Urgent message getters
  const currentUrgentMessage = computed(() => {
    return (
      urgentMessages.value.find(
        (msg) => !acknowledgedMessageIds.value.has(msg.id),
      ) || null
    )
  })

  const hasUrgentMessages = computed(() => {
    return urgentMessages.value.some(
      (msg) => !acknowledgedMessageIds.value.has(msg.id),
    )
  })

  const unacknowledgedUrgentCount = computed(() => {
    return urgentMessages.value.filter(
      (msg) => !acknowledgedMessageIds.value.has(msg.id),
    ).length
  })

  // ========================
  // Actions
  // ========================

  /**
   * Initialize the store - fetch inbox and unread counts
   */
  async function initialize() {
    if (isInitialized.value) return
    if (initializationPromise) return initializationPromise

    initializationPromise = (async () => {
      isInitializing.value = true
      error.value = null

      try {
        const [inboxResponse, unreadCountsResponse] = await Promise.all([
          getInbox(),
          getUnreadCounts(),
        ])

        inbox.value = inboxResponse.items
        unreadCounts.value = {
          totalUnreadCount: unreadCountsResponse.total,
          unreadByScope: inboxResponse.unreadByScope,
          announcementUnreadCount: unreadCountsResponse.announcements ?? 0,
        }
        isInitialized.value = true
      } catch (e) {
        error.value = 'Failed to initialize messaging'
        console.error('Messaging initialization error:', e)
      } finally {
        isInitializing.value = false
        initializationPromise = undefined
      }
    })()

    return initializationPromise
  }

  /**
   * Refresh inbox data
   */
  async function refreshInbox(scope?: ConversationScope) {
    isRefreshingInbox.value = true
    error.value = null

    try {
      const [inboxResponse, unreadCountsResponse] = await Promise.all([
        getInbox(scope),
        getUnreadCounts(),
      ])
      inbox.value = inboxResponse.items
      unreadCounts.value = {
        totalUnreadCount: unreadCountsResponse.total,
        unreadByScope: inboxResponse.unreadByScope,
        announcementUnreadCount: unreadCountsResponse.announcements ?? 0,
      }
    } catch (e) {
      error.value = 'Failed to refresh inbox'
      console.error('Refresh inbox error:', e)
    } finally {
      isRefreshingInbox.value = false
    }
  }

  /**
   * Refresh unread counts without fetching the full inbox
   */
  async function refreshUnreadCounts() {
    try {
      const response = await getUnreadCounts()
      unreadCounts.value = {
        ...unreadCounts.value,
        totalUnreadCount: response.total,
        announcementUnreadCount: response.announcements ?? 0,
      }
    } catch (e) {
      console.error('Refresh unread counts error:', e)
    }
  }

  /**
   * Invalidate cached directory query data so the next render refetches.
   * Used together with inbox refresh when room staff visibility changes.
   */
  function invalidateDirectoryQueries() {
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.MessagingFamilyDirectory],
    })
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.MessagingInternalDirectory],
    })
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.MessagingGroups],
    })
  }

  /**
   * Mark inbox + directory as stale so they will be refreshed the next time
   * messaging is opened. Called from attendance SignalR events when messaging
   * is not currently visible.
   */
  function markInboxStale() {
    isInboxStale.value = true
  }

  /**
   * If the inbox has been marked stale, refresh inbox data and invalidate the
   * directory caches so heavy directory queries refetch on demand. Safe to
   * call unconditionally — it no-ops when not stale.
   */
  async function refreshIfStale() {
    if (!isInboxStale.value) return
    isInboxStale.value = false
    invalidateDirectoryQueries()
    await refreshInbox()
  }

  /**
   * Load a conversation
   */
  async function loadConversation(
    conversationId: string,
    forceRefresh = false,
    silent = false,
    context: 'drawer' | 'fullPage' = 'fullPage',
  ) {
    const slot = getActiveSlot(context)

    if (!forceRefresh && conversationCache.value.has(conversationId)) {
      slot.id.value = conversationId
      slot.conv.value = conversationCache.value.get(conversationId)!
      return
    }

    slot.id.value = conversationId
    slot.conv.value = null

    if (!silent) {
      isLoadingConversation.value = true
    }
    error.value = null

    try {
      const conversation = await getConversation(conversationId)
      // Clear any previous forbidden flag — access may have been restored
      // (e.g. room staff signed back into the relevant space).
      if (forbiddenConversationIds.value.has(conversationId)) {
        const next = new Set(forbiddenConversationIds.value)
        next.delete(conversationId)
        forbiddenConversationIds.value = next
      }
      conversationCache.value.set(conversationId, conversation)
      if (slot.id.value !== conversationId) return
      slot.id.value = conversationId
      slot.conv.value = conversation
    } catch (e) {
      if (slot.id.value !== conversationId) return
      const status = (e as { response?: { status?: number } })?.response?.status
      if (status === STATUS_CODES.forbidden) {
        // Mark as forbidden so the view can render a "no permission" state
        const next = new Set(forbiddenConversationIds.value)
        next.add(conversationId)
        forbiddenConversationIds.value = next
        // Surface the id in the slot so the view component can react.
        slot.id.value = conversationId
        slot.conv.value = null
        // Drop any stale cached copy.
        conversationCache.value.delete(conversationId)

        // If a room staff user hits a forbidden conversation AND the inbox
        // was already marked stale by an attendance change, refresh
        if (useAuthStore().isRoomStaff && isInboxStale.value) {
          isInboxStale.value = false
          invalidateDirectoryQueries()
          void refreshInbox()
        }
      } else {
        if (slot.id.value === conversationId) {
          slot.conv.value = null
        }
        error.value = 'Failed to load conversation'
      }
    } finally {
      if (!silent) {
        isLoadingConversation.value = false
      }
    }
  }

  /**
   * Returns true when the server has denied access to the given conversation for
   * the current user. Consumers (e.g. ConversationView) can use this to render
   * a dedicated "you don't have permission" state.
   */
  function isForbidden(conversationId: string | null | undefined): boolean {
    if (!conversationId) return false
    return forbiddenConversationIds.value.has(conversationId)
  }

  /**
   * Load more messages in active conversation (pagination)
   */
  async function loadMoreMessages(context: 'drawer' | 'fullPage' = 'fullPage') {
    const slot = getActiveSlot(context)
    if (!slot.conv.value || !slot.conv.value.continuationToken) {
      return
    }

    isLoadingMoreMessages.value = true

    try {
      const response = await getConversation(
        slot.id.value!,
        50,
        slot.conv.value.continuationToken,
      )

      const updatedConversation: ConversationModel = {
        ...slot.conv.value,
        messages: [...response.messages, ...slot.conv.value.messages],
        continuationToken: response.continuationToken,
      }

      conversationCache.value.set(slot.id.value!, updatedConversation)
      updateActiveSlots(slot.id.value!, updatedConversation)
    } catch (e) {
      error.value = 'Failed to load more messages'
      console.error('Load more messages error:', e)
    } finally {
      isLoadingMoreMessages.value = false
    }
  }

  /**
   * Send a message with optimistic update
   */
  async function sendMessageAction(
    conversationId: string,
    body: string,
    actingAsGroupId?: string,
    isUrgent?: boolean,
  ): Promise<MessageModel | null> {
    // Generate temporary ID for optimistic update
    const tempId = `temp-${Date.now()}-${Math.random().toString(36).slice(2)}`

    // For internal group conversations, use the own group name
    const cachedConv = conversationCache.value.get(conversationId)
    const isInternalScope = cachedConv?.scope === ConversationScope.Internal
    const groupDisplayName =
      isInternalScope && cachedConv?.groupName ? cachedConv.groupName : null

    // Create optimistic message
    const optimisticMessage: PendingMessageModel = {
      id: tempId,
      conversationId,
      content: body,
      senderUserId: 'current-user',
      senderActingAsGroupId: actingAsGroupId ?? null,
      senderActingAsGroupName: groupDisplayName,
      timestamp: new Date().toISOString(),
      isOwnMessage: true,
      isUrgent: isUrgent ?? false,
      isRead: true,
      readAt: null,
      editedAt: null,
      broadcastMessageId: null,
      isLegacy: false,
      displayName: groupDisplayName ?? 'You',
      sendStatus: 'sending',
    }

    // Add to pending
    pendingMessages.value.set(tempId, optimisticMessage)

    try {
      const request: SendMessageRequest = {
        conversationId,
        content: body,
        actingAsGroupId,
        isUrgent,
      }

      const sentMessage = await sendMessage(request)

      // Remove from pending
      pendingMessages.value.delete(tempId)

      if (isViewingConversation(conversationId)) {
        const cachedConv = conversationCache.value.get(conversationId)
        if (cachedConv) {
          const updatedConversation: ConversationModel = {
            ...cachedConv,
            messages: [...cachedConv.messages, sentMessage],
          }
          conversationCache.value.set(conversationId, updatedConversation)
          updateActiveSlots(conversationId, updatedConversation)
        }
      }

      // Update inbox item — for INTERNAL scope, build the "Partner (via OwnGroup)" format
      // and use the group name as the sender display name.
      const cachedConvForInbox = conversationCache.value.get(conversationId)
      const senderAuthStore = useAuthStore()
      let resolvedPartnerName: string | undefined
      let resolvedSenderName: string = 'You'
      if (cachedConvForInbox?.scope === ConversationScope.Internal) {
        const partner = cachedConvForInbox.participantName
        const own = cachedConvForInbox.groupName
        if (partner) {
          resolvedPartnerName = own ? `${partner} (via ${own})` : partner
        }
        resolvedSenderName = own ?? 'You'
      } else if (cachedConvForInbox?.participantName) {
        resolvedPartnerName = cachedConvForInbox.participantName
      }
      updateInboxForNewMessage(
        conversationId,
        body,
        resolvedSenderName,
        false,
        cachedConvForInbox?.scope,
        undefined,
        resolvedPartnerName,
        senderAuthStore.userEmail,
      )

      return sentMessage
    } catch (e) {
      // Mark as failed
      const failedMessage = pendingMessages.value.get(tempId)
      if (failedMessage) {
        pendingMessages.value.set(tempId, {
          ...failedMessage,
          sendStatus: 'failed',
        })
      }
      error.value = 'Failed to send message'
      console.error('Send message error:', e)
      return null
    }
  }

  /**
   * Send broadcast message to multiple families
   */
  async function sendBroadcastMessageAction(
    request: SendBroadcastMessageRequest,
  ): Promise<BroadcastMessageModel | null> {
    isSendingBroadcast.value = true
    error.value = null

    try {
      const result = await sendBroadcastMessage(request)

      // Set sending status for the broadcast conversation
      if (result?.broadcastConversationId) {
        broadcastSendingStatuses.value.set(result.broadcastConversationId, {
          status: 'sending',
          totalRecipients: request.memberIds.length,
          successCount: 0,
          failedCount: 0,
          failedRecipients: [],
        })
      }

      // Refresh inbox to show new conversations
      await refreshInbox()

      return result
    } catch (e) {
      error.value = 'Failed to send broadcast message'
      console.error('Send broadcast message error:', e)
      return null
    } finally {
      isSendingBroadcast.value = false
    }
  }

  /**
   * Load broadcast conversation history (messages to same set of recipients)
   */
  async function loadBroadcastConversationAction(
    broadcastConversationId: string,
  ): Promise<BroadcastConversationModel | null> {
    isLoadingBroadcastConversation.value = true
    error.value = null

    try {
      const result = await getBroadcastConversation(broadcastConversationId)
      activeBroadcastConversation.value = result
      return result
    } catch (e) {
      error.value = 'Failed to load broadcast conversation'
      console.error('Load broadcast conversation error:', e)
      return null
    } finally {
      isLoadingBroadcastConversation.value = false
    }
  }

  /**
   * Send a new message to an existing broadcast conversation (same recipients)
   */
  async function sendBroadcastFromConversationAction(
    content: string,
    actingAsGroupId?: string,
  ): Promise<BroadcastMessageModel | null> {
    const conv = activeBroadcastConversation.value
    if (!conv || !conv.recipients.length) return null

    isSendingBroadcast.value = true
    error.value = null

    try {
      const result = await sendBroadcastMessage({
        content,
        memberIds: conv.recipients.map((r) => r.memberId),
        scope: conv.scope,
        actingAsGroupId,
      })

      // Set sending status for the broadcast conversation
      if (result?.broadcastConversationId) {
        broadcastSendingStatuses.value.set(result.broadcastConversationId, {
          status: 'sending',
          totalRecipients: conv.recipients.length,
          successCount: 0,
          failedCount: 0,
          failedRecipients: [],
        })
      }

      return result
    } catch (e) {
      error.value = 'Failed to send broadcast message'
      console.error('Send broadcast from conversation error:', e)
      return null
    } finally {
      isSendingBroadcast.value = false
    }
  }

  /**
   * Start a direct conversation with another admin
   */
  async function startDirectConversationAction(
    request: StartDirectConversationRequest,
  ): Promise<MessageModel | null> {
    isStartingDirectConversation.value = true
    error.value = null

    try {
      const result = await startDirectConversation(request)

      // Refresh inbox to show the new direct conversation
      await refreshInbox()

      return result
    } catch (e) {
      error.value = 'Failed to start direct conversation'
      console.error('Start direct conversation error:', e)
      return null
    } finally {
      isStartingDirectConversation.value = false
    }
  }

  // ========================
  // Group Chat Actions
  // ========================
  function invalidateGroupChatDirectory() {
    void queryClient.invalidateQueries({
      queryKey: [QueryKey.MessagingAdminMessagingDirectory],
    })
  }

  async function createGroupChatAction(
    request: CreateGroupChatRequest,
  ): Promise<GroupChatModel | null> {
    error.value = null
    try {
      const result = await createGroupChat(request)
      setGroupChatDisplayName(result.conversationId, result.name)
      await refreshInbox()
      invalidateGroupChatDirectory()
      return result
    } catch (e) {
      error.value = 'Failed to create group chat'
      console.error('Create group chat error:', e)
      return null
    }
  }

  async function getGroupChatAction(
    conversationId: string,
  ): Promise<GroupChatModel | null> {
    try {
      const result = await getGroupChat(conversationId)
      setGroupChatDisplayName(conversationId, result?.name)
      return result
    } catch (e) {
      console.error('Get group chat error:', e)
      return null
    }
  }

  async function updateGroupChatAction(
    conversationId: string,
    request: UpdateGroupChatRequest,
  ): Promise<GroupChatModel | null> {
    try {
      const result = await updateGroupChat(conversationId, request)
      invalidateGroupChatDirectory()
      setGroupChatDisplayName(conversationId, result.name)
      if (request.name !== undefined) {
        updateInboxPartnerName(conversationId, result.name)
        updateConversationParticipantName(conversationId, result.name)
      }
      return result
    } catch (e) {
      error.value = 'Failed to update group chat'
      console.error('Update group chat error:', e)
      return null
    }
  }

  async function addGroupChatMembersAction(
    conversationId: string,
    request: AddGroupChatMembersRequest,
  ): Promise<GroupChatModel | null> {
    try {
      const result = await addGroupChatMembers(conversationId, request)
      invalidateGroupChatDirectory()
      return result
    } catch (e) {
      error.value = 'Failed to add members'
      console.error('Add group chat members error:', e)
      return null
    }
  }

  async function removeGroupChatMemberAction(
    conversationId: string,
    request: RemoveGroupChatMemberRequest,
  ): Promise<boolean> {
    try {
      await removeGroupChatMember(conversationId, request)
      invalidateGroupChatDirectory()
      return true
    } catch (e) {
      const responseData = (e as { response?: { data?: unknown } }).response
        ?.data
      if (
        responseData &&
        typeof responseData === 'object' &&
        ('message' in responseData || 'errors' in responseData)
      ) {
        fail(
          responseData as {
            message: string
            errors: {
              errorCode: string
              errorMessage: string
              propertyName: string
            }[]
          },
        )
      } else {
        fail('Failed to remove member')
      }
      error.value = 'Failed to remove member'
      console.error('Remove group chat member error:', e)
      return false
    }
  }

  async function leaveGroupChatAction(
    conversationId: string,
  ): Promise<boolean> {
    try {
      await leaveGroupChat(conversationId)
      invalidateGroupChatDirectory()
      // Remove from inbox and clear conversation if active
      inbox.value = inbox.value.filter(
        (i) => i.conversationId !== conversationId,
      )
      conversationCache.value.delete(conversationId)
      if (activeConversationId.value === conversationId) {
        activeConversationId.value = null
        activeConversation.value = null
      }
      if (drawerConversationId.value === conversationId) {
        drawerConversationId.value = null
        drawerConversation.value = null
      }
      return true
    } catch (e) {
      const responseData = (e as { response?: { data?: unknown } }).response
        ?.data
      if (
        responseData &&
        typeof responseData === 'object' &&
        ('message' in responseData || 'errors' in responseData)
      ) {
        fail(
          responseData as {
            message: string
            errors: {
              errorCode: string
              errorMessage: string
              propertyName: string
            }[]
          },
        )
      } else {
        fail('Failed to leave group chat')
      }
      error.value = 'Failed to leave group chat'
      console.error('Leave group chat error:', e)
      return false
    }
  }

  async function updateGroupChatMemberAction(
    conversationId: string,
    request: UpdateGroupChatMemberRequest,
  ): Promise<GroupChatModel | null> {
    try {
      return await updateGroupChatMember(conversationId, request)
    } catch (e) {
      error.value = 'Failed to update member history'
      console.error('Update group chat member error:', e)
      return null
    }
  }

  async function updateGroupChatAdminsAction(
    conversationId: string,
    adminUserIds: string[],
  ): Promise<GroupChatModel | null> {
    try {
      return await updateGroupChatAdmins(conversationId, adminUserIds)
    } catch (e) {
      error.value = 'Failed to update group chat admins'
      console.error('Update group chat admins error:', e)
      return null
    }
  }

  async function setGroupChatHiddenAction(
    conversationId: string,
    hidden: boolean,
  ): Promise<boolean> {
    try {
      await setGroupChatHidden(conversationId, hidden)
      invalidateGroupChatDirectory()
      inbox.value = inbox.value.map((item) =>
        item.conversationId === conversationId
          ? { ...item, hiddenFromDirectory: hidden }
          : item,
      )
      return true
    } catch (e) {
      error.value = 'Failed to update hidden group chats'
      console.error('Set group chat hidden error:', e)
      return false
    }
  }

  async function deleteGroupChatAction(
    conversationId: string,
  ): Promise<boolean> {
    try {
      await deleteGroupChat(conversationId)
      invalidateGroupChatDirectory()
      inbox.value = inbox.value.filter(
        (item) => item.conversationId !== conversationId,
      )
      conversationCache.value.delete(conversationId)
      return true
    } catch (e) {
      error.value = 'Failed to delete group chat'
      console.error('Delete group chat error:', e)
      return false
    }
  }

  /**
   * Edit a message
   */
  async function editMessageAction(
    conversationId: string,
    messageId: string,
    body: string,
  ): Promise<MessageModel | null> {
    try {
      const updatedMessage = await editMessage(conversationId, messageId, {
        content: body,
      })

      if (isViewingConversation(conversationId)) {
        const cachedConv = conversationCache.value.get(conversationId)
        if (cachedConv) {
          const messages = cachedConv.messages.map((m) =>
            m.id === messageId ? updatedMessage : m,
          )
          const updatedConversation: ConversationModel = {
            ...cachedConv,
            messages,
          }
          conversationCache.value.set(conversationId, updatedConversation)
          updateActiveSlots(conversationId, updatedConversation)
        }
      }

      // Update the inbox preview
      const inboxIdx = inbox.value.findIndex(
        (i) => i.conversationId === conversationId,
      )
      if (inboxIdx >= 0) {
        const inboxItem = inbox.value[inboxIdx]
        if (inboxItem.lastMessageSenderId === updatedMessage.senderUserId) {
          const cachedConv = conversationCache.value.get(conversationId)
          const editedMsg = cachedConv?.messages.find((m) => m.id === messageId)
          const isLastFromSender = editedMsg
            ? !cachedConv!.messages.some(
              (m) =>
                m.senderUserId === updatedMessage.senderUserId &&
                m.id !== messageId &&
                new Date(m.timestamp) > new Date(editedMsg.timestamp),
            )
            : true
          if (isLastFromSender) {
            const items = [...inbox.value]
            items[inboxIdx] = {
              ...inboxItem,
              lastMessage: updatedMessage.content,
            }
            inbox.value = items
          }
        }
      }

      return updatedMessage
    } catch (e) {
      error.value = 'Failed to edit message'
      console.error('Edit message error:', e)
      return null
    }
  }

  /**
   * Mark message as read
   */
  async function markAsRead(conversationId: string, messageId: string) {
    try {
      await markMessageAsRead(conversationId, messageId)

      // Decrement unread count
      decrementUnreadCount(conversationId)
    } catch (e) {
      console.error('Mark as read error:', e)
    }
  }

  /**
   * Mark conversation as unread (sets the unread flag)
   */
  async function markAsUnread(
    conversationId: string,
    viewContext: 'drawer' | 'fullPage' = 'fullPage',
  ): Promise<{ count: number; firstUnreadId: string | null }> {
    try {
      const newUnreadCount = await markConversationAsUnread(conversationId)

      const idx = inbox.value.findIndex(
        (i) => i.conversationId === conversationId,
      )
      if (idx >= 0) {
        const delta = newUnreadCount - inbox.value[idx].unreadCount
        const items = [...inbox.value]
        items[idx] = {
          ...items[idx],
          unreadCount: newUnreadCount,
          markedUnread: true,
        }
        inbox.value = items
        if (delta !== 0) {
          unreadCounts.value.totalUnreadCount += delta
          updateScopeUnreadCount(items[idx].scope, delta)
        }
      }

      markMessagesUnreadInCache(conversationId, newUnreadCount)
      const firstUnreadId = findFirstUnreadId(
        conversationId,
        newUnreadCount,
        viewContext,
      )
      return { count: newUnreadCount, firstUnreadId }
    } catch (e) {
      console.error('Mark as unread error:', e)
      return { count: 0, firstUnreadId: null }
    }
  }

  /**
   * Mark a specific message and all messages after it as unread
   */
  async function markMessageUnread(conversationId: string, messageId: string) {
    try {
      const newUnreadCount = await markMessageAsUnread(
        conversationId,
        messageId,
      )

      const idx = inbox.value.findIndex(
        (i) => i.conversationId === conversationId,
      )
      if (idx >= 0) {
        const delta = newUnreadCount - inbox.value[idx].unreadCount
        const items = [...inbox.value]
        items[idx] = {
          ...items[idx],
          unreadCount: newUnreadCount,
          markedUnread: true,
        }
        inbox.value = items
        if (delta !== 0) {
          unreadCounts.value.totalUnreadCount += delta
          updateScopeUnreadCount(items[idx].scope, delta)
        }
      }

      markMessagesUnreadFromId(conversationId, messageId)
    } catch (e) {
      console.error('Mark message as unread error:', e)
    }
  }

  /**
   * Mark all messages in conversation as read
   */
  async function markConversationAsRead(conversationId: string) {
    try {
      await markAllAsRead(conversationId)

      // Update inbox (create new array so shallowRef triggers reactivity)
      const idx = inbox.value.findIndex(
        (i) => i.conversationId === conversationId,
      )
      if (idx >= 0) {
        const oldCount = inbox.value[idx].unreadCount
        const items = [...inbox.value]
        items[idx] = { ...items[idx], unreadCount: 0, markedUnread: false }
        inbox.value = items

        // Update total counts
        unreadCounts.value.totalUnreadCount -= oldCount
        updateScopeUnreadCount(items[idx].scope, -oldCount)
      }

      // Mark all messages as read in cache — must create new objects to trigger shallowRef reactivity
      const markAllRead = (conv: ConversationModel): ConversationModel => ({
        ...conv,
        messages: conv.messages.map((m) =>
          m.isRead ? m : { ...m, isRead: true },
        ),
      })

      const cached = conversationCache.value.get(conversationId)
      if (cached) {
        const updated = markAllRead(cached)
        conversationCache.value.set(conversationId, updated)
        updateActiveSlots(conversationId, updated)
      }
    } catch (e) {
      console.error('Mark all as read error:', e)
    }
  }

  /**
   * Mark all inbox conversations as read (optionally filtered by scope).
   * Returns the undo token and modified count, or null on failure.
   */
  async function markAllInboxAsReadAction(
    scopes?: ConversationScope[],
  ): Promise<{ undoToken: string; modifiedCount: number } | null> {
    try {
      // Snapshot current state for optimistic UI
      readAllUndoSnapshot.value = [...inbox.value]
      readAllUndoPreviousCounts.value = { ...unreadCounts.value }

      // Optimistic update: zero unread on matching items
      const items = [...inbox.value]
      let totalDelta = 0
      const scopeDeltas: Record<string, number> = {}
      const scopeSet = scopes ? new Set(scopes as string[]) : null

      for (let i = 0; i < items.length; i++) {
        const item = items[i]
        const matchesScope = !scopeSet || scopeSet.has(item.scope as string)

        if (matchesScope && (item.unreadCount > 0 || item.markedUnread)) {
          const delta = item.unreadCount
          totalDelta += delta
          const key = item.scope as string
          scopeDeltas[key] = (scopeDeltas[key] ?? 0) + delta
          items[i] = { ...item, unreadCount: 0, markedUnread: false }
        }
      }

      inbox.value = items
      unreadCounts.value = {
        ...unreadCounts.value,
        totalUnreadCount: Math.max(
          0,
          unreadCounts.value.totalUnreadCount - totalDelta,
        ),
        unreadByScope: Object.fromEntries(
          Object.entries(unreadCounts.value.unreadByScope).map(([k, v]) => [
            k,
            Math.max(0, v - (scopeDeltas[k] ?? 0)),
          ]),
        ),
      }

      // Call backend
      const result = await markAllInboxAsRead(scopes)

      readAllUndoToken.value = result.undoToken
      readAllUndoModifiedCount.value = result.modifiedCount

      return result
    } catch (e) {
      // Revert optimistic update on failure
      if (readAllUndoSnapshot.value.length > 0) {
        inbox.value = readAllUndoSnapshot.value
      }
      if (readAllUndoPreviousCounts.value) {
        unreadCounts.value = readAllUndoPreviousCounts.value
      }
      readAllUndoToken.value = null
      readAllUndoModifiedCount.value = 0
      readAllUndoSnapshot.value = []
      readAllUndoPreviousCounts.value = null
      console.error('Mark all inbox as read error:', e)
      return null
    }
  }

  /**
   * Undo the previous "mark all inbox as read" operation.
   */
  async function undoMarkAllInboxAsReadAction(): Promise<boolean> {
    const token = readAllUndoToken.value
    if (!token) return false

    try {
      // Optimistic revert: restore snapshot
      if (readAllUndoSnapshot.value.length > 0) {
        inbox.value = readAllUndoSnapshot.value
      }
      if (readAllUndoPreviousCounts.value) {
        unreadCounts.value = readAllUndoPreviousCounts.value
      }

      await undoMarkAllInboxAsRead(token)

      return true
    } catch (e) {
      // If undo fails (e.g. 409 expired), refresh from server
      console.error('Undo mark all inbox as read error:', e)
      await refreshInbox()
      return false
    } finally {
      readAllUndoToken.value = null
      readAllUndoModifiedCount.value = 0
      readAllUndoSnapshot.value = []
      readAllUndoPreviousCounts.value = null
    }
  }

  /**
   * Clear the undo state (called when the undo window expires).
   */
  function clearReadAllUndo() {
    readAllUndoToken.value = null
    readAllUndoModifiedCount.value = 0
    readAllUndoSnapshot.value = []
    readAllUndoPreviousCounts.value = null
  }

  /**
   * Load announcements
   */
  async function loadAnnouncements(
    pageSize = 20,
    view?: 'outbox',
    outboxFilter?: 'mine' | 'client',
  ) {
    isLoadingAnnouncements.value = true
    error.value = null

    try {
      const response = await getAnnouncements(
        pageSize,
        undefined,
        view,
        outboxFilter,
      )
      announcements.value = response.items
      pinnedAnnouncementsState.value = response.pinnedItems ?? []
      announcementsContinuationToken.value = response.continuationToken ?? null
    } catch (e) {
      error.value = 'Failed to load announcements'
      console.error('Load announcements error:', e)
    } finally {
      isLoadingAnnouncements.value = false
    }
  }

  /**
   * Load more announcements (pagination)
   */
  async function loadMoreAnnouncements(
    view?: 'outbox',
    outboxFilter?: 'mine' | 'client',
  ) {
    if (!announcementsContinuationToken.value) return

    isLoadingMoreAnnouncements.value = true

    try {
      const response = await getAnnouncements(
        20,
        announcementsContinuationToken.value,
        view,
        outboxFilter,
      )
      announcements.value = [...announcements.value, ...response.items]
      announcementsContinuationToken.value = response.continuationToken ?? null
    } catch (e) {
      error.value = 'Failed to load more announcements'
      console.error('Load more announcements error:', e)
    } finally {
      isLoadingMoreAnnouncements.value = false
    }
  }

  /**
   * Send a new announcement
   */
  async function sendAnnouncementAction(payload: SendAnnouncementRequest) {
    isSendingAnnouncement.value = true
    error.value = null

    try {
      const newAnnouncement = await sendAnnouncement(payload)
      // Prepend to local list
      announcements.value = [newAnnouncement, ...announcements.value]
      return newAnnouncement
    } catch (e) {
      error.value = 'Failed to send announcement'
      console.error('Send announcement error:', e)
      throw e
    } finally {
      isSendingAnnouncement.value = false
    }
  }

  /**
   * Delete an announcement
   */
  async function deleteAnnouncementAction(announcementId: string) {
    try {
      await deleteAnnouncement(announcementId)
      announcements.value = announcements.value.filter(
        (a) => a.id !== announcementId,
      )
    } catch (e) {
      console.error('Delete announcement error:', e)
      throw e
    }
  }

  /**
   * Edit an existing announcement
   */
  async function editAnnouncementAction(
    announcementId: string,
    payload: EditAnnouncementRequest,
  ) {
    try {
      const updated = await editAnnouncement(announcementId, payload)
      announcements.value = announcements.value.map((a) =>
        a.id === announcementId ? updated : a,
      )
      return updated
    } catch (e) {
      console.error('Edit announcement error:', e)
      throw e
    }
  }

  /**
   * Mark all announcements as read — clears announcement unread count
   */
  async function markAnnouncementsAsRead() {
    // Clear optimistically so the badge drops immediately
    const announcementDelta = unreadCounts.value.announcementUnreadCount
    unreadCounts.value = {
      ...unreadCounts.value,
      announcementUnreadCount: 0,
      totalUnreadCount: Math.max(
        0,
        unreadCounts.value.totalUnreadCount - announcementDelta,
      ),
    }
    hasNewAnnouncementSignal.value = 0

    try {
      await markAllAnnouncementsRead()
    } catch (e) {
      // Restore on failure
      unreadCounts.value = {
        ...unreadCounts.value,
        announcementUnreadCount: announcementDelta,
        totalUnreadCount:
          unreadCounts.value.totalUnreadCount + announcementDelta,
      }
      console.error('Mark announcements as read error:', e)
    }
  }

  /**
   * Load broadcast message details for activity tab
   */
  async function loadBroadcastMessage(broadcastMessageId: string) {
    isLoadingBroadcastMessage.value = true

    try {
      const result = await getBroadcastMessage(broadcastMessageId)
      activeBroadcastMessage.value = result
      return result
    } catch (e) {
      error.value = 'Failed to load broadcast message'
      console.error('Load broadcast message error:', e)
      return null
    } finally {
      isLoadingBroadcastMessage.value = false
    }
  }

  /**
   * Search messages in a conversation
   */
  async function searchConversationMessages(
    conversationId: string,
    params: SearchMessagesParams,
  ): Promise<ConversationModel | null> {
    isSearchingMessages.value = true
    error.value = null

    try {
      const result = await searchMessages(conversationId, params)
      return result
    } catch (e) {
      error.value = 'Failed to search messages'
      console.error('Search messages error:', e)
      return null
    } finally {
      isSearchingMessages.value = false
    }
  }

  /**
   * Clear active conversation
   */
  function clearActiveConversation(
    context: 'drawer' | 'fullPage' = 'fullPage',
  ) {
    const slot = getActiveSlot(context)
    slot.id.value = null
    slot.conv.value = null
  }

  /**
   * Clear all state (logout)
   */
  function reset() {
    activeToastDialogs.value.forEach((dialog) => {
      try {
        dialog.hide()
      } catch (error) {
        console.error('Failed to hide message toast during reset:', error)
      }
    })
    activeToastDialogs.value = []
    regularToastQueue.value = []
    urgentToastQueue.value = []
    isRegularToastActive.value = false
    isUrgentToastActive.value = false
    isInitialized.value = false
    isInitializing.value = false
    isRefreshingInbox.value = false
    isLoadingConversation.value = false
    isLoadingMoreMessages.value = false
    isSendingBroadcast.value = false
    isStartingDirectConversation.value = false
    isLoadingAnnouncements.value = false
    isLoadingMoreAnnouncements.value = false
    isSendingAnnouncement.value = false
    isLoadingBroadcastMessage.value = false
    isSearchingMessages.value = false
    error.value = null
    inbox.value = []
    unreadCounts.value = {
      totalUnreadCount: 0,
      unreadByScope: {},
      announcementUnreadCount: 0,
    }
    activeConversationId.value = null
    activeConversation.value = null
    drawerConversationId.value = null
    drawerConversation.value = null
    conversationCache.value.clear()
    announcements.value = []
    announcementsContinuationToken.value = null
    hasNewAnnouncementSignal.value = 0
    activeBroadcastMessage.value = null
    pendingMessages.value.clear()
    // Reset urgent messages
    urgentMessages.value = []
    acknowledgedMessageIds.value.clear()
    forbiddenConversationIds.value.clear()
    queryClient.removeQueries({ queryKey: [QueryKey.MessagingAdminDirectory] })
    queryClient.removeQueries({
      queryKey: [QueryKey.MessagingAdminMessagingDirectory],
    })
    queryClient.removeQueries({ queryKey: [QueryKey.MessagingFamilyDirectory] })
    queryClient.removeQueries({
      queryKey: [QueryKey.MessagingInternalDirectory],
    })
    queryClient.removeQueries({ queryKey: [QueryKey.MessagingGroups] })
  }

  // ========================
  // Urgent Message Actions
  // ========================

  /**
   * Add a new urgent message notification
   */
  function addUrgentMessage(message: Omit<UrgentMessage, 'receivedAt'>) {
    const newMessage: UrgentMessage = {
      ...message,
      receivedAt: new Date(),
    }
    urgentMessages.value.push(newMessage)
  }

  /**
   * Acknowledge the current (oldest unacknowledged) urgent message
   */
  function acknowledgeCurrentUrgentMessage() {
    if (currentUrgentMessage.value) {
      const messageId = currentUrgentMessage.value.id
      const conversationId = currentUrgentMessage.value.conversationId
      acknowledgedMessageIds.value.add(messageId)

      // Persist to backend (fire-and-forget)
      if (conversationId) {
        void acknowledgeUrgentConversation(conversationId)
      }

      // Clean up old acknowledged messages (keep last 50)
      if (acknowledgedMessageIds.value.size > 50) {
        const oldestMessages = urgentMessages.value
          .filter((msg) => acknowledgedMessageIds.value.has(msg.id))
          .slice(0, 10)

        oldestMessages.forEach((msg) => {
          acknowledgedMessageIds.value.delete(msg.id)
          const index = urgentMessages.value.findIndex((m) => m.id === msg.id)
          if (index > -1) {
            urgentMessages.value.splice(index, 1)
          }
        })
      }
    }
  }

  /**
   * Acknowledge a specific urgent message by ID
   */
  function acknowledgeUrgentMessage(messageId: string) {
    acknowledgedMessageIds.value.add(messageId)

    void acknowledgeUrgentConversation(messageId)
  }

  async function showUnacknowledgedUrgentToasts() {
    const authStore = useAuthStore()
    const userConfigStore = useUserConfigStore()
    const defaultShowToast = !authStore.isSuperAdmin
    const showUrgentToast =
      userConfigStore.get<boolean>(
        'messaging.showUrgentToast',
        defaultShowToast,
      ) ?? defaultShowToast

    if (!showUrgentToast) return

    try {
      const inboxResponse = await getInbox()
      inbox.value = inboxResponse.items

      const unreadUrgentItems = inboxResponse.items.filter(
        (item) =>
          item.isUrgent &&
          item.unreadCount > 0 &&
          !item.urgentAcknowledged &&
          !acknowledgedMessageIds.value.has(item.conversationId) &&
          !urgentMessages.value.some(
            (m) => m.conversationId === item.conversationId,
          ) &&
          !urgentToastQueue.value.some(
            (t) => t.conversationId === item.conversationId,
          ),
      )

      for (const item of unreadUrgentItems) {
        addUrgentMessage({
          id: item.conversationId,
          conversationId: item.conversationId,
          senderName: item.partnerName,
          senderId: item.lastMessageSenderId ?? '',
          content: item.lastMessage ?? '',
        })

        showMessageToast({
          senderName: item.partnerName,
          content: item.lastMessage ?? '',
          isUrgent: true,
          conversationId: item.conversationId,
          onDismiss: () => acknowledgeUrgentMessage(item.conversationId),
        })
      }
    } catch (e) {
      console.error('Failed to fetch urgent messages on reconnect:', e)
    }
  }

  // Helper to update scope-specific unread count
  function updateScopeUnreadCount(scope: ConversationScope, delta: number) {
    const scopeKey = scope as string
    const current = unreadCounts.value.unreadByScope[scopeKey] ?? 0
    unreadCounts.value.unreadByScope[scopeKey] = Math.max(0, current + delta)
  }

  /**
   * Check if a message is from the "other side" of a conversation.
   * For ROOM/ADMIN scope, only parent/member messages count as "other side" for staff.
   * For DIRECT/INTERNAL scope, any non-own message is from the other side.
   */
  function isOtherSideMessage(
    m: MessageModel,
    conv: ConversationModel | null,
  ): boolean {
    if (m.kind === 'system') return false
    if (m.isOwnMessage) return false
    if (
      conv &&
      (conv.scope === ConversationScope.Room ||
        conv.scope === ConversationScope.Admin) &&
      conv.memberId
    ) {
      return m.senderUserId === conv.memberId
    }
    return true
  }

  function markMessagesUnreadInCache(
    conversationId: string,
    count: number,
  ): string | null {
    let firstMarkedId: string | null = null
    const markLastN = (
      messages: MessageModel[],
      conv: ConversationModel | null,
    ) => {
      const sorted = [...messages].sort(
        (a, b) =>
          new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
      )
      let remaining = count
      for (let i = sorted.length - 1; i >= 0 && remaining > 0; i--) {
        if (isOtherSideMessage(sorted[i], conv)) {
          sorted[i].isRead = false
          firstMarkedId = sorted[i].id
          remaining--
        }
      }
    }

    const cached = conversationCache.value.get(conversationId)
    if (cached) {
      markLastN(cached.messages, cached)
    }
    if (
      activeConversation.value &&
      activeConversationId.value === conversationId
    ) {
      markLastN(activeConversation.value.messages, activeConversation.value)
    }
    if (
      drawerConversation.value &&
      drawerConversationId.value === conversationId
    ) {
      markLastN(drawerConversation.value.messages, drawerConversation.value)
    }
    return firstMarkedId
  }

  function markMessagesUnreadFromId(conversationId: string, messageId: string) {
    const updateMessages = (
      messages: MessageModel[],
      conv: ConversationModel | null,
    ) => {
      const idx = messages.findIndex((m) => m.id === messageId)
      if (idx < 0) return
      for (let i = idx; i < messages.length; i++) {
        if (isOtherSideMessage(messages[i], conv)) {
          messages[i].isRead = false
        }
      }
    }

    const cached = conversationCache.value.get(conversationId)
    if (cached) {
      updateMessages(cached.messages, cached)
    }
    if (
      activeConversation.value &&
      activeConversationId.value === conversationId
    ) {
      updateMessages(
        activeConversation.value.messages,
        activeConversation.value,
      )
    }
    if (
      drawerConversation.value &&
      drawerConversationId.value === conversationId
    ) {
      updateMessages(
        drawerConversation.value.messages,
        drawerConversation.value,
      )
    }
  }

  /**
   * Find the first (earliest) "other-side" unread message ID by counting
   * `count` messages from the end. Returns null until enough paged messages
   * are loaded to identify the boundary reliably. Does NOT mutate state.
   */
  function findFirstUnreadId(
    conversationId: string,
    count: number,
    viewContext: 'drawer' | 'fullPage' = 'fullPage',
  ): string | null {
    const conv =
      viewContext === 'drawer'
        ? drawerConversation.value
        : (activeConversation.value ??
          conversationCache.value.get(conversationId) ??
          null)
    if (!conv || count <= 0) return null
    const sorted = [...conv.messages].sort(
      (a, b) =>
        new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime(),
    )
    let remaining = count
    let firstId: string | null = null
    for (let i = sorted.length - 1; i >= 0 && remaining > 0; i--) {
      if (isOtherSideMessage(sorted[i], conv)) {
        firstId = sorted[i].id
        remaining--
      }
    }
    return remaining === 0 ? firstId : null
  }

  // ========================
  // SignalR Handlers
  // ========================

  /**
   * Handle incoming message from SignalR
   */
  function handleIncomingMessage(message: MessageModel) {
    if (conversationCache.value.has(message.conversationId)) {
      const conversation = conversationCache.value.get(message.conversationId)!
      const updatedConversation: ConversationModel = {
        ...conversation,
        messages: [...conversation.messages, message],
      }
      conversationCache.value.set(message.conversationId, updatedConversation)
      updateActiveSlots(message.conversationId, updatedConversation)
    }

    const viewing = isViewingConversation(message.conversationId)
    const inboxEntry = inbox.value.find(
      (i) => i.conversationId === message.conversationId,
    )
    const isMarkedUnread = inboxEntry?.markedUnread ?? false
    const shouldIncrementUnread =
      !viewing || !isNearBottom.value || isMarkedUnread
    const cachedConv = conversationCache.value.get(message.conversationId)
    updateInboxForNewMessage(
      message.conversationId,
      message.content,
      message.displayName,
      shouldIncrementUnread,
      cachedConv?.scope,
      undefined,
      undefined,
      message.senderUserId,
    )
  }

  function handleGroupChatSystemMessageEvent(
    payload: GroupChatSystemMessageSignalRPayload,
    orgId?: string,
  ) {
    const authStore = useAuthStore()
    if (orgId && authStore.currentOrgId && orgId !== authStore.currentOrgId) {
      return
    }

    const message: MessageModel = {
      id: payload.id,
      conversationId: payload.conversationId,
      senderUserId: payload.senderUserId,
      senderActingAsGroupId: payload.senderActingAsGroupId,
      senderActingAsGroupName: payload.senderActingAsGroupName,
      content: payload.content,
      timestamp: payload.timestamp,
      kind: payload.kind,
      systemMessageType: payload.systemMessageType,
      actorUserId: payload.actorUserId,
      actorDisplayName: payload.actorDisplayName,
      targetDisplayNames: payload.targetDisplayNames,
      systemMetadata: payload.systemMetadata,
      isRead: payload.isRead,
      readAt: payload.readAt,
      editedAt: payload.editedAt,
      broadcastMessageId: payload.broadcastMessageId,
      isOwnMessage: false,
      isUrgent: false,
      isLegacy: false,
      displayName: payload.displayName,
    }

    if (conversationCache.value.has(payload.conversationId)) {
      const conversation = conversationCache.value.get(payload.conversationId)!
      if (conversation.messages.some((m) => m.id === message.id)) return

      const updatedConversation: ConversationModel = {
        ...conversation,
        messages: [...conversation.messages, message],
      }
      conversationCache.value.set(payload.conversationId, updatedConversation)
      updateActiveSlots(payload.conversationId, updatedConversation)
    } else if (isViewingConversation(payload.conversationId)) {
      if (activeConversationId.value === payload.conversationId) {
        void loadConversation(payload.conversationId, true, true, 'fullPage')
      }
      if (drawerConversationId.value === payload.conversationId) {
        void loadConversation(payload.conversationId, true, true, 'drawer')
      }
    }

    if (payload.systemMessageType === SystemMessageType.GroupChatNameChanged) {
      const updatedName = payload.systemMetadata?.newName?.trim()
      if (updatedName) {
        setGroupChatDisplayName(payload.conversationId, updatedName)
        updateInboxPartnerName(payload.conversationId, updatedName)
        updateConversationParticipantName(payload.conversationId, updatedName)
      }
    }

    if (isViewingConversation(payload.conversationId)) {
      latestIncomingMessage.value = {
        conversationId: payload.conversationId,
        messageId: payload.id,
        senderName: payload.actorDisplayName ?? payload.displayName,
        content: payload.content,
        isUrgent: false,
        isSystem: true,
        timestamp: Date.now(),
      }
    }
  }

  /**
   * Handle inbox update from SignalR
   */
  function handleInboxUpdate(item: InboxItemModel) {
    const existingIndex = inbox.value.findIndex(
      (i) => i.conversationId === item.conversationId,
    )

    if (existingIndex >= 0) {
      // Update existing
      const items = [...inbox.value]
      items[existingIndex] = item
      inbox.value = items
    } else {
      // Add new
      inbox.value = [item, ...inbox.value]
    }
  }

  /**
   * Handle newMessage SignalR event from user-level connection
   */
  function handleNewMessageEvent(
    payload: NewMessageSignalRPayload,
    orgId?: string,
    centerId?: string,
  ) {
    const authStore = useAuthStore()
    const defaultShowToast = !authStore.isSuperAdmin

    // Direct and GroupChat scoped messages are org-level
    const isOrgLevelScope =
      payload.scope?.toLowerCase() === (ConversationScope.Direct as string) ||
      payload.scope?.toLowerCase() === (ConversationScope.GroupChat as string)

    // Skip messages from a different org entirely
    if (
      !authStore.isClient &&
      orgId &&
      authStore.currentOrgId &&
      orgId !== authStore.currentOrgId
    ) {
      return
    }

    // Check org/center context — skip processing if the message is from a different center
    // (unless it's a client, which only has one center context)
    if (!authStore.isClient && !isOrgLevelScope && orgId && centerId) {
      const isSameCenter = centerId === authStore.currentCenterId
      if (!isSameCenter) {
        // Cross-center message: only show a toast if enabled
        if (!payload.isUrgent && !payload.viewerOnly) {
          const userConfigStore = useUserConfigStore()
          const showCrossCenterToast =
            userConfigStore.get<boolean>(
              'messaging.showCrossCenterToast',
              defaultShowToast,
            ) ?? defaultShowToast
          const disabledCenters =
            userConfigStore.get<string[]>(
              'messaging.disabledToastCenters',
              [],
            ) ?? []
          if (showCrossCenterToast && !disabledCenters.includes(centerId)) {
            showMessageToast({
              senderName:
                payload.senderActingAsGroupName || payload.senderDisplayName,
              content: payload.content,
              isUrgent: false,
              conversationId: payload.conversationId ?? '',
              centerId,
            })
          }
        }
        return
      }
    }

    latestIncomingMessage.value = {
      conversationId: payload.conversationId ?? '',
      messageId: payload.messageId ?? '',
      senderName: payload.senderActingAsGroupName || payload.senderDisplayName,
      content: payload.content,
      isUrgent: payload.isUrgent ?? false,
      timestamp: Date.now(),
    }

    if (authStore.isClient) {
      const clientMessagingStore = useClientMessagingStore()
      if (payload.broadcastMessageId) {
        void clientMessagingStore.refreshUnreadCounts()
      } else if (!clientMessagingStore.isMessengerOpen) {
        clientMessagingStore.totalUnreadCount++
        if (payload.conversationId) {
          clientMessagingStore.pendingUnreadByConversation = {
            ...clientMessagingStore.pendingUnreadByConversation,
            [payload.conversationId]:
              (clientMessagingStore.pendingUnreadByConversation[
                payload.conversationId
              ] ?? 0) + 1,
          }
        }
      }
      return
    }

    if (payload.conversationId) {
      // viewerOnly events are sent to staff/admins who share a conversation but
      // weren't primary recipients. Only refresh the conversation if viewing it;
      // skip inbox, unread, and toast updates entirely.
      if (payload.viewerOnly) {
        if (isViewingConversation(payload.conversationId)) {
          if (activeConversationId.value === payload.conversationId) {
            void loadConversation(
              payload.conversationId,
              true,
              true,
              'fullPage',
            )
          }
          if (drawerConversationId.value === payload.conversationId) {
            void loadConversation(payload.conversationId, true, true, 'drawer')
          }
        }
        const senderName =
          payload.senderActingAsGroupName || payload.senderDisplayName
        updateInboxForNewMessage(
          payload.conversationId,
          payload.content,
          senderName,
          false,
          payload.scope as ConversationScope | undefined,
          payload.isUrgent,
          payload.partnerName,
          payload.senderUserId,
        )
        return
      }

      const viewing = isViewingConversation(payload.conversationId)
      const senderName =
        payload.senderActingAsGroupName || payload.senderDisplayName
      // Increment unread if not viewing, not near bottom, or the conversation was manually marked unread
      const inboxEntry = inbox.value.find(
        (i) => i.conversationId === payload.conversationId,
      )
      const isMarkedUnread = inboxEntry?.markedUnread ?? false
      const shouldIncrementUnread =
        !viewing || !isNearBottom.value || isMarkedUnread
      updateInboxForNewMessage(
        payload.conversationId,
        payload.content,
        senderName,
        shouldIncrementUnread,
        payload.scope as ConversationScope | undefined,
        payload.isUrgent,
        payload.partnerName,
        payload.senderUserId,
      )

      if (viewing) {
        if (activeConversationId.value === payload.conversationId) {
          void loadConversation(payload.conversationId, true, true, 'fullPage')
        }
        if (drawerConversationId.value === payload.conversationId) {
          void loadConversation(payload.conversationId, true, true, 'drawer')
        }
      } else {
        conversationCache.value.delete(payload.conversationId)
      }
    } else if (payload.broadcastMessageId) {
      // Broadcast messages don't include a per-recipient conversationId in the
      // batched SignalR event. Refresh the inbox so the server-created inbox
      // entries (with correct unread counts) are picked up.
      void refreshInbox()
    }

    if (!payload.isUrgent) {
      const userConfigStore = useUserConfigStore()
      const showRegularToast =
        userConfigStore.get<boolean>(
          'messaging.showRegularToast',
          defaultShowToast,
        ) ?? defaultShowToast
      const messagingStore = useMessagingStore()
      const currentlyViewing = payload.conversationId
        ? isViewingConversation(payload.conversationId)
        : false
      if (showRegularToast && !messagingStore.isOpen && !currentlyViewing) {
        showMessageToast({
          senderName:
            payload.senderActingAsGroupName || payload.senderDisplayName,
          content: payload.content,
          isUrgent: false,
          conversationId: payload.conversationId ?? '',
        })
      }
    }
  }

  /**
   * Handle urgentMessage SignalR event from user-level connection
   */
  function handleUrgentMessageEvent(
    payload: NewMessageSignalRPayload,
    orgId?: string,
    centerId?: string,
  ) {
    const authStore = useAuthStore()
    const defaultShowToast = !authStore.isSuperAdmin

    // Direct and GroupChat scoped messages are org-level
    const isOrgLevelScope =
      payload.scope?.toLowerCase() === (ConversationScope.Direct as string) ||
      payload.scope?.toLowerCase() === (ConversationScope.GroupChat as string)

    // Skip messages from a different org entirely
    if (
      !authStore.isClient &&
      orgId &&
      authStore.currentOrgId &&
      orgId !== authStore.currentOrgId
    ) {
      return
    }

    // Check org/center context for cross-center urgent messages
    if (!authStore.isClient && !isOrgLevelScope && orgId && centerId) {
      const isSameCenter = centerId === authStore.currentCenterId
      if (!isSameCenter) {
        const userConfigStore = useUserConfigStore()
        const showCrossCenterToast =
          userConfigStore.get<boolean>(
            'messaging.showCrossCenterToast',
            defaultShowToast,
          ) ?? defaultShowToast
        const playUrgentSound = shouldPlayUrgentMessageSound(defaultShowToast)
        const disabledCenters =
          userConfigStore.get<string[]>('messaging.disabledToastCenters', []) ??
          []
        if (
          !payload.viewerOnly &&
          showCrossCenterToast &&
          !disabledCenters.includes(centerId)
        ) {
          showMessageToast({
            senderName:
              payload.senderActingAsGroupName || payload.senderDisplayName,
            content: payload.content,
            isUrgent: true,
            conversationId: payload.conversationId || '',
            centerId,
            onDismiss: () => {
              if (payload.conversationId) {
                void acknowledgeUrgentConversation(
                  payload.conversationId,
                  centerId,
                )
              }
            },
          })
        }

        if (!payload.viewerOnly && playUrgentSound) {
          void playNotificationSound(NotificationSoundKey.UrgentMessage)
        }

        return
      }
    }

    // viewerOnly urgent messages: only refresh the open conversation
    if (payload.viewerOnly) {
      payload = { ...payload, isUrgent: true }
      handleNewMessageEvent(payload, orgId, centerId)
      return
    }

    const msgId =
      payload.messageId || payload.broadcastMessageId || `urgent-${Date.now()}`

    addUrgentMessage({
      id: msgId,
      conversationId: payload.conversationId || '',
      senderName: payload.senderActingAsGroupName || payload.senderDisplayName,
      senderId: '',
      content: payload.content,
    })

    const userConfigStore = useUserConfigStore()
    const showUrgentToast =
      userConfigStore.get<boolean>(
        'messaging.showUrgentToast',
        defaultShowToast,
      ) ?? defaultShowToast
    const playUrgentSound = shouldPlayUrgentMessageSound(defaultShowToast)

    if (playUrgentSound) {
      void playNotificationSound(NotificationSoundKey.UrgentMessage)
    }

    if (showUrgentToast) {
      showMessageToast({
        senderName:
          payload.senderActingAsGroupName || payload.senderDisplayName,
        content: payload.content,
        isUrgent: true,
        conversationId: payload.conversationId || '',
        onDismiss: () => acknowledgeCurrentUrgentMessage(),
      })
    } else {
      acknowledgeCurrentUrgentMessage()
    }

    // Also process as a regular new message (but skip the regular toast)
    payload = { ...payload, isUrgent: true }
    handleNewMessageEvent(payload, orgId, centerId)
  }

  /**
   * Handle messageRead SignalR event — a recipient has read our message
   */
  function handleMessageReadEvent(payload: MessageReadSignalRPayload) {
    if (!conversationCache.value.has(payload.conversationId)) return

    const conversation = conversationCache.value.get(payload.conversationId)!
    const readMessage = conversation.messages.find(
      (m) => m.id === payload.messageId,
    )
    const readTimestamp = readMessage?.timestamp ?? payload.readAt
    const messages = conversation.messages.map((m) =>
      !m.isRead && m.timestamp <= readTimestamp
        ? { ...m, isRead: true, readAt: m.readAt ?? payload.readAt }
        : m,
    )
    const updatedConversation: ConversationModel = {
      ...conversation,
      messages,
    }
    conversationCache.value.set(payload.conversationId, updatedConversation)

    updateActiveSlots(payload.conversationId, updatedConversation)
  }

  /**
   * Handle messageUnread SignalR event — a recipient has unmarked our message
   */
  function handleMessageUnreadEvent(payload: MessageUnreadSignalRPayload) {
    if (!conversationCache.value.has(payload.conversationId)) return

    const conversation = conversationCache.value.get(payload.conversationId)!
    const messages = conversation.messages.map((m) =>
      m.id === payload.messageId ? { ...m, isRead: false, readAt: null } : m,
    )
    const updatedConversation: ConversationModel = {
      ...conversation,
      messages,
    }
    conversationCache.value.set(payload.conversationId, updatedConversation)

    updateActiveSlots(payload.conversationId, updatedConversation)
  }

  /**
   * Handle messageEdited SignalR event — a message in a conversation was edited
   */
  function handleMessageEditedEvent(payload: MessageEditedSignalRPayload) {
    // Update conversation cache if loaded
    if (conversationCache.value.has(payload.conversationId)) {
      const conversation = conversationCache.value.get(payload.conversationId)!
      const messages = conversation.messages.map((m) =>
        m.id === payload.messageId
          ? { ...m, content: payload.content, editedAt: payload.editedAt }
          : m,
      )
      const updatedConversation: ConversationModel = {
        ...conversation,
        messages,
      }
      conversationCache.value.set(payload.conversationId, updatedConversation)

      updateActiveSlots(payload.conversationId, updatedConversation)
    }

    // Update the inbox preview if the edited message is the last message shown
    // The inbox only shows the last message from this sender (not other staff),
    // so check if there's any newer message from the same sender in the cache.
    const inboxIdx = inbox.value.findIndex(
      (i) => i.conversationId === payload.conversationId,
    )
    if (inboxIdx >= 0) {
      const inboxItem = inbox.value[inboxIdx]
      if (inboxItem.lastMessageSenderId === payload.senderUserId) {
        const cachedConv = conversationCache.value.get(payload.conversationId)
        const editedMsg = cachedConv?.messages.find(
          (m) => m.id === payload.messageId,
        )
        const isLastFromSender = editedMsg
          ? !cachedConv!.messages.some(
            (m) =>
              m.senderUserId === payload.senderUserId &&
              m.id !== payload.messageId &&
              new Date(m.timestamp) > new Date(editedMsg.timestamp),
          )
          : true
        if (isLastFromSender) {
          const items = [...inbox.value]
          items[inboxIdx] = { ...inboxItem, lastMessage: payload.content }
          inbox.value = items
        }
      }
    }
  }

  /**
   * Handle newAnnouncement SignalR event — refresh announcements list and increment unread count
   */
  function handleNewAnnouncementEvent(
    payload: NewAnnouncementSignalRPayload,
    orgId?: string,
    centerId?: string,
  ) {
    const authStore = useAuthStore()

    // Skip messages from a different org entirely
    if (
      !authStore.isClient &&
      orgId &&
      authStore.currentOrgId &&
      orgId !== authStore.currentOrgId
    ) {
      return
    }

    // Check org/center context — skip if message is from a different center
    if (!authStore.isClient && orgId && centerId) {
      if (centerId !== authStore.currentCenterId) return
    }

    if (authStore.isClient) {
      const clientContextStore = useClientContextStore()

      if (payload.roles.length > 0 && !payload.roles.includes(authStore.role)) {
        return
      }

      const students = clientContextStore.students

      const { studentStatuses, groupIds } = payload

      if (studentStatuses !== null && studentStatuses.length > 0) {
        const hasMatchingStatus = students.some(
          (s) => s.status && studentStatuses.includes(s.status),
        )
        if (!hasMatchingStatus) return
      }

      if (groupIds !== null && groupIds.length > 0) {
        const hasMatchingGroup = students.some(
          (s) => s.group?.id && groupIds.includes(s.group.id),
        )
        if (!hasMatchingGroup) return
      }

      const clientMessagingStore = useClientMessagingStore()
      void clientMessagingStore.handleNewAnnouncement()
      return
    }

    if (payload.roles.length > 0 && !payload.roles.includes(authStore.role)) {
      return
    }

    hasNewAnnouncementSignal.value++

    unreadCounts.value = {
      ...unreadCounts.value,
      announcementUnreadCount: unreadCounts.value.announcementUnreadCount + 1,
      totalUnreadCount: unreadCounts.value.totalUnreadCount + 1,
    }
    void loadAnnouncements()
  }

  /**
   * Handle broadcastSendingComplete SignalR event — update sending status for the broadcast
   */
  function handleBroadcastSendingCompleteEvent(
    payload: BroadcastSendingCompletePayload,
  ) {
    if (payload.failedCount === 0) {
      // All delivered — remove from tracking so the message naturally shows "Sent"
      broadcastSendingStatuses.value.delete(payload.broadcastConversationId)
    } else {
      broadcastSendingStatuses.value.set(payload.broadcastConversationId, {
        status: 'complete',
        totalRecipients: payload.totalRecipients,
        successCount: payload.successCount,
        failedCount: payload.failedCount,
        failedRecipients: payload.failedRecipients.map((r) => r.memberName),
      })
    }
  }

  // ========================
  // Helper Functions
  // ========================

  function resolveScope(
    scope: string | undefined,
    conversationId: string,
  ): ConversationScope {
    const raw =
      scope ?? conversationCache.value.get(conversationId)?.scope ?? ''
    const normalised = raw.toLowerCase() as ConversationScope
    if (
      normalised === ConversationScope.Room ||
      normalised === ConversationScope.Admin ||
      normalised === ConversationScope.Internal ||
      normalised === ConversationScope.Direct ||
      normalised === ConversationScope.GroupChat
    ) {
      return normalised
    }
    return ConversationScope.Room
  }

  function updateInboxForNewMessage(
    conversationId: string,
    preview: string,
    senderName: string,
    incrementUnread = false,
    scope?: ConversationScope,
    isUrgent?: boolean,
    partnerName?: string,
    senderId?: string,
  ) {
    const existingIndex = inbox.value.findIndex(
      (i) => i.conversationId === conversationId,
    )
    const resolvedScope = resolveScope(scope, conversationId)
    const cachedConversation = conversationCache.value.get(conversationId)
    const resolvedGroupChatName =
      partnerName ??
      groupChatDisplayNames.value.get(conversationId) ??
      cachedConversation?.participantName
    const resolvedPartnerName =
      resolvedScope === ConversationScope.GroupChat
        ? (resolvedGroupChatName ?? 'Group chat')
        : (partnerName ?? cachedConversation?.participantName ?? senderName)

    const truncatedPreview =
      preview && preview.length > 150 ? preview.slice(0, 150) : preview

    if (existingIndex >= 0) {
      const items = [...inbox.value]
      const item = { ...items[existingIndex] }
      item.lastMessageTime = new Date().toISOString()
      item.lastMessage = truncatedPreview
      item.lastMessageSenderName = senderName
      item.lastMessageSenderId = senderId ?? item.lastMessageSenderId
      if (isUrgent !== undefined) item.isUrgent = isUrgent
      if (incrementUnread) {
        item.unreadCount++
        unreadCounts.value.totalUnreadCount++
        updateScopeUnreadCount(item.scope, 1)
      }
      // Move updated item to top (most recently updated)
      items.splice(existingIndex, 1)
      inbox.value = [item, ...items]
    } else {
      // Create a new synthetic inbox item so the conversation appears immediately.
      const isStaffScope =
        resolvedScope === ConversationScope.Internal ||
        resolvedScope === ConversationScope.Direct ||
        resolvedScope === ConversationScope.GroupChat
      const newItem: InboxItemModel = {
        id: `inbox-${conversationId}`,
        conversationId,
        partnerName: resolvedPartnerName,
        partnerAvatar: null,
        lastMessage: truncatedPreview,
        lastMessageTime: new Date().toISOString(),
        lastMessageSenderName: senderName,
        lastMessageSenderId: senderId ?? null,
        unreadCount: incrementUnread ? 1 : 0,
        scope: resolvedScope,
        participantType: isStaffScope ? 'staff' : 'family',
        isTemporary: false,
        markedUnread: false,
        isBroadcastSent: false,
        broadcastMessageId: null,
        broadcastRecipientsPreview: null,
        broadcastRecipientsCount: null,
        familyId: null,
        isUrgent: isUrgent ?? false,
        urgentAcknowledged: false,
        groupChatParticipantsPreview: null,
        hiddenFromDirectory: false,
      }
      inbox.value = [newItem, ...inbox.value]
      if (incrementUnread) {
        unreadCounts.value.totalUnreadCount++
        updateScopeUnreadCount(newItem.scope, 1)
      }
    }
  }

  function decrementUnreadCount(conversationId: string) {
    const idx = inbox.value.findIndex(
      (i) => i.conversationId === conversationId,
    )
    if (idx >= 0 && inbox.value[idx].unreadCount > 0) {
      const items = [...inbox.value]
      items[idx] = { ...items[idx], unreadCount: items[idx].unreadCount - 1 }
      inbox.value = items
      unreadCounts.value.totalUnreadCount = Math.max(
        0,
        unreadCounts.value.totalUnreadCount - 1,
      )
      updateScopeUnreadCount(items[idx].scope, -1)
    }
  }

  // ========================
  // Return
  // ========================
  return {
    // State
    isInitialized,
    // Granular loading states
    isInitializing,
    isRefreshingInbox,
    isLoadingConversation,
    isLoadingMoreMessages,
    isSendingBroadcast,
    isStartingDirectConversation,
    isLoadingAnnouncements,
    isLoadingMoreAnnouncements,
    isSendingAnnouncement,
    isLoadingBroadcastMessage,
    isLoadingBroadcastConversation,
    isSearchingMessages,
    // Computed aggregate — true if any operation is in flight
    isLoading,
    error,
    inbox,
    unreadCounts,
    activeConversationId,
    activeConversation,
    drawerConversationId,
    drawerConversation,
    isNearBottom,
    announcements,
    hasNewAnnouncementSignal,
    activeBroadcastMessage,
    activeBroadcastConversation,
    pendingMessages,
    urgentMessages,

    // Getters
    hasUnreadMessages,
    inboxItems,
    hasMoreMessages,
    roomInboxItems,
    adminInboxItems,
    familyInboxItems,
    directInboxItems,
    internalInboxItems,
    groupChatInboxItems,
    hiddenGroupChatInboxItems,
    activeMessages,
    pinnedAnnouncements,
    regularAnnouncements,
    // Urgent message getters
    currentUrgentMessage,
    hasUrgentMessages,
    unacknowledgedUrgentCount,

    // Latest incoming message (for toast)
    latestIncomingMessage,

    // Toast queue state
    regularToastQueueSize,
    urgentToastQueueSize,
    isUrgentToastActive,

    // Forbidden conversation tracking
    forbiddenConversationIds,
    isForbidden,

    // Stale-data tracking (room staff inbox/directory invalidation)
    isInboxStale,
    markInboxStale,
    refreshIfStale,

    // Actions
    initialize,
    refreshInbox,
    refreshUnreadCounts,
    loadConversation,
    loadMoreMessages,
    sendMessageAction,
    sendBroadcastMessageAction,
    loadBroadcastConversationAction,
    sendBroadcastFromConversationAction,
    editMessageAction,
    markAsRead,
    markAsUnread,
    markMessageUnread,
    markConversationAsRead,
    markAllInboxAsReadAction,
    undoMarkAllInboxAsReadAction,
    clearReadAllUndo,
    readAllUndoToken,
    readAllUndoModifiedCount,
    findFirstUnreadId,
    loadAnnouncements,
    loadMoreAnnouncements,
    sendAnnouncementAction,
    deleteAnnouncementAction,
    editAnnouncementAction,
    markAnnouncementsAsRead,
    loadBroadcastMessage,
    searchConversationMessages,
    clearActiveConversation,
    isViewingConversation,
    startDirectConversationAction,
    createGroupChatAction,
    getGroupChatAction,
    updateGroupChatAction,
    addGroupChatMembersAction,
    removeGroupChatMemberAction,
    leaveGroupChatAction,
    updateGroupChatMemberAction,
    updateGroupChatAdminsAction,
    setGroupChatHiddenAction,
    deleteGroupChatAction,
    reset,
    // Urgent message actions
    addUrgentMessage,
    acknowledgeCurrentUrgentMessage,
    acknowledgeUrgentMessage,
    showUnacknowledgedUrgentToasts,

    // SignalR Handlers
    handleIncomingMessage,
    handleGroupChatSystemMessageEvent,
    handleInboxUpdate,
    handleNewMessageEvent,
    handleUrgentMessageEvent,
    handleMessageReadEvent,
    handleMessageUnreadEvent,
    handleMessageEditedEvent,
    handleNewAnnouncementEvent,
    handleBroadcastSendingCompleteEvent,

    // Broadcast sending status
    broadcastSendingStatuses,
  }
})
